import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { AuthUser, LoginInput, Permission } from '@najah/core'
import { authApi, setAccessToken, setRefreshToken, getRefreshToken, setSessionExpiredHandler } from './api'

interface AuthContextValue {
  user: AuthUser | null
  /** يُعيد جلب بيانات المستخدم بعد تغيير يمسّها */
  refreshProfile: () => Promise<void>
  /** أثناء محاولة استعادة الجلسة عند الإقلاع */
  isRestoring: boolean
  login: (input: LoginInput) => Promise<void>
  logout: () => Promise<void>
  can: (permission: Permission) => boolean
  canAny: (permissions: Permission[]) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isRestoring, setIsRestoring] = useState(true)

  // استعادة الجلسة عند الإقلاع من رمز التحديث المحفوظ
  useEffect(() => {
    let cancelled = false

    async function restore() {
      if (!getRefreshToken()) {
        if (!cancelled) setIsRestoring(false)
        return
      }
      try {
        await authApi.refresh()
        const me = await authApi.me()
        if (!cancelled) setUser(me)
      } catch {
        // الرمز منتهٍ أو مُبطل — نبدأ من شاشة الدخول
        if (!cancelled) setUser(null)
      } finally {
        if (!cancelled) setIsRestoring(false)
      }
    }

    void restore()
    return () => {
      cancelled = true
    }
  }, [])

  // أي فشل تحديث لاحق في أي طلب يُخرج المستخدم فوراً
  useEffect(() => {
    setSessionExpiredHandler(() => setUser(null))
    return () => setSessionExpiredHandler(null)
  }, [])

  const login = useCallback(async (input: LoginInput) => {
    const res = await authApi.login(input)
    setAccessToken(res.accessToken)
    setRefreshToken(res.refreshToken)
    setUser(res.user)
  }, [])

  const refreshProfile = useCallback(async () => {
    try {
      setUser(await authApi.me())
    } catch {
      setUser(null)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // الخروج محلياً يتم في كل الأحوال، حتى لو تعذّر إبلاغ الخادم
    }
    setAccessToken(null)
    setRefreshToken(null)
    setUser(null)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isRestoring,
      login,
      logout,
      refreshProfile,
      can: (p) => !!user?.permissions.includes(p),
      canAny: (ps) => !!user && ps.some((p) => user.permissions.includes(p)),
    }),
    [user, isRestoring, login, logout, refreshProfile],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth يجب أن يُستعمل داخل AuthProvider')
  return ctx
}
